import { Injectable } from '@angular/core';
import { TranslateService as NgxTranslateService } from '@ngx-translate/core';
import { LocalStorageConstants } from '../shared/constants/localStorage.constants';
import { LanguageEnum } from '../enums/language.enum';
import { getEnumList } from '../_helpers/enum-helper';

@Injectable({
  providedIn: 'root'
})
export class TranslateService {
  constructor(private translate: NgxTranslateService) {
    this.useDefaultLang();
  }

  useDefaultLang(): LanguageEnum {
    const storedLang = localStorage.getItem(LocalStorageConstants.language);
    const lang = this.isLanguageEnum(storedLang) ? storedLang : LanguageEnum.English;
    this.changeLanguage(lang);
    return lang;
  }

  isLanguageEnum(value: string | null): value is LanguageEnum {
    return value !== null && Object.values(LanguageEnum).includes(value as LanguageEnum);
  }

  changeLanguage(language: LanguageEnum): void {
    if (this.translate.currentLang !== language) {
      this.translate.use(language);
      localStorage.setItem(LocalStorageConstants.language, language);
    }
  }

  // ngx-translate throws on an empty/undefined key. Callers resolve keys from enum
  // reverse-lookups, so an unmapped value would throw mid-render and abort the whole change
  // detection pass — stalling every other view on screen, not just the offending cell.
  getDynamicTranslation(key: string, params: Record<string, unknown> = {}): string {
    if (!key) return '';
    return this.translate.instant(key, params);
  }

  getLanguageList(): (keyof typeof LanguageEnum)[] {
    return getEnumList(LanguageEnum) as (keyof typeof LanguageEnum)[];
  }

  getSelectedLanguageLabel(): string | undefined {
    const currentLang = this.translate.currentLang as LanguageEnum;
    return this.getLanguageList().find(key => LanguageEnum[key] === currentLang);
  }

  getCurrentLanguage(): LanguageEnum {
    return (this.translate.currentLang as LanguageEnum) ?? LanguageEnum.English;
  }
}
